import React, { useState } from 'react';
import { useI18n } from '../../context/I18nContext';
import { getCableColorForSVG, formatSezione } from '../../utils/cableLogic';
import { Icons } from '../icons/Icons';
import ColorSelector from './ColorSelector';
import SectionSelector from './SectionSelector';
import TerminalSelector from './TerminalSelector';

export const PoloEditor = ({ polo, index, onUpdate, onRemove, onDuplicate, settings, terminalData, canRemove, addMorsettoDirection, addVirtualConnection, swapAB }) => {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = useState(index === 0);

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header polo */}
      <div
        className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getCableColorForSVG(polo.colore) }}></div>
          <span className="font-medium">
            {t('cableForm.pole')} {index + 1} - {polo.colore} {formatSezione(polo.sezione, settings.useAWG)}
          </span>
          {(polo.siglaturaA || polo.siglaturaB) && (
            <span className="text-sm text-gray-500">
              ({polo.siglaturaA || '—'} → {polo.siglaturaB || '—'})
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(polo.id);
            }}
            className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
            title={t('cableForm.duplicatePole')}
          >
            <Icons.Copy className="w-4 h-4" />
          </button>

          {canRemove && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove(polo.id);
              }}
              className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
              title={t('cableForm.removePole')}
            >
              <Icons.Trash2 className="w-4 h-4" />
            </button>
          )}

          <span className="text-gray-400">
            {isExpanded ? '▼' : '▷'}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Riga 1: Colore e Sezione */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('cableForm.color')}</label>
              <ColorSelector
                value={polo.colore}
                onChange={(color) => onUpdate(polo.id, 'colore', color)}
                language={settings.lingua}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('cableForm.section')}</label>
              <SectionSelector
                value={polo.sezione}
                onChange={(sezione) => onUpdate(polo.id, 'sezione', sezione)}
                useAWG={settings.useAWG}
                currentColor={polo.colore}
                className="w-full"
              />
            </div>
          </div>

          {/* Riga 2: Siglature */}
          {!settings.marcaturePerCollegamento && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('cableForm.poleSignatureA')}</label>
                <input
                  type="text"
                  value={polo.siglaturaA}
                  onChange={(e) => onUpdate(polo.id, 'siglaturaA', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder={t('cableForm.placeholderPoleSignatureA')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('cableForm.poleSignatureB')}</label>
                <input
                  type="text"
                  value={polo.siglaturaB}
                  onChange={(e) => onUpdate(polo.id, 'siglaturaB', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder={t('cableForm.placeholderPoleSignatureB')}
                />
              </div>
            </div>
          )}

          {/* Riga 3: Lunghezze */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('cableForm.poleLengthA')}</label>
              <input
                type="number"
                value={polo.lunghezzaA}
                onChange={(e) => onUpdate(polo.id, 'lunghezzaA', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('cableForm.poleLengthB')}</label>
              <input
                type="number"
                value={polo.lunghezzaB}
                onChange={(e) => onUpdate(polo.id, 'lunghezzaB', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Riga 4: Terminali */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('cableForm.terminalA')}</label>
              <TerminalSelector
                value={polo.terminaleA}
                onChange={(terminal) => onUpdate(polo.id, 'terminaleA', terminal)}
                terminali={terminalData}
                settings={settings}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('cableForm.terminalB')}</label>
              <TerminalSelector
                value={polo.terminaleB}
                onChange={(terminal) => onUpdate(polo.id, 'terminaleB', terminal)}
                terminali={terminalData}
                settings={settings}
                className="w-full"
              />
            </div>
          </div>
          {/* Riga 5: Collegamenti con pulsante swap */}
          <div className="flex items-end gap-4">
            <div className="flex-1 relative">
              <label className="block text-sm font-medium mb-1">{t('cableForm.connectionA')}</label>
              <input
                type="text"
                value={polo.collegamentoA}
                onChange={(e) => onUpdate(polo.id, 'collegamentoA', e.target.value)}
                className="w-full px-3 py-2 pr-12 border rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder={t('cableForm.placeholderConnectionA')}
              />

              {/* Frecce UP/DOWN con poloId */}
              <div className="absolute right-1 top-7 bottom-1 flex flex-row gap-0.5">
                <button
                  type="button"
                  onClick={() => addVirtualConnection('collegamentoA', polo.id)}
                  className="px-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded text-xs font-bold transition-colors flex items-center justify-center"
                  title={t('cableForm.addVirtualDot')}
                >
                  ·
                </button>
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => addMorsettoDirection('collegamentoA', 'up', polo.id)}
                    className="flex-1 px-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-t text-xs font-bold transition-colors flex items-center justify-center"
                    title={t('cableForm.addArrowUp')}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => addMorsettoDirection('collegamentoA', 'down', polo.id)}
                    className="flex-1 px-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-b text-xs font-bold transition-colors flex items-center justify-center"
                    title={t('cableForm.addArrowDown')}
                  >
                    ▼
                  </button>
                </div>
              </div>
            </div>

            {/* 🔄 PULSANTE SWAP al centro */}
            <div className="flex-shrink-0 pb-1">
              <button
                type="button"
                onClick={() => swapAB && swapAB(polo.id)}
                className="bg-white border-2 border-blue-300 hover:border-blue-500 text-blue-600 hover:text-blue-800 p-2 rounded-full shadow-sm hover:shadow-md transition-all duration-200 group"
                title={t('cableForm.swapTerminals')}
              >
                <Icons.Swap className="w-4 h-4 group-hover:scale-110 transition-transform" />
              </button>
            </div>

            <div className="flex-1 relative">
              <label className="block text-sm font-medium mb-1">{t('cableForm.connectionB')}</label>
              <input
                type="text"
                value={polo.collegamentoB}
                onChange={(e) => onUpdate(polo.id, 'collegamentoB', e.target.value)}
                className="w-full px-3 py-2 pr-12 border rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder={t('cableForm.placeholderConnectionB')}
              />

              {/* Frecce UP/DOWN con poloId */}
              <div className="absolute right-1 top-7 bottom-1 flex flex-row gap-0.5">
                <button
                  type="button"
                  onClick={() => addVirtualConnection('collegamentoB', polo.id)}
                  className="px-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded text-xs font-bold transition-colors flex items-center justify-center"
                  title={t('cableForm.addVirtualDot')}
                >
                  ·
                </button>
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => addMorsettoDirection('collegamentoB', 'up', polo.id)}
                    className="flex-1 px-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-t text-xs font-bold transition-colors flex items-center justify-center"
                    title={t('cableForm.addArrowUp')}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => addMorsettoDirection('collegamentoB', 'down', polo.id)}
                    className="flex-1 px-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-b text-xs font-bold transition-colors flex items-center justify-center"
                    title={t('cableForm.addArrowDown')}
                  >
                    ▼
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};